import { useState, useRef } from 'react';
import { api } from '@/lib/api';
import { OwnerDocument } from '@/lib/types';
import { Upload, FileText, Trash2, Download, Loader2, Eye, Pencil, Check, X } from 'lucide-react';
import { classNames } from '@/lib/utils';

interface DocumentSectionProps {
  title: string;
  ownerId: string;
  category: 'licencia-turistica' | 'contrato' | 'otros';
  documents: OwnerDocument[];
  onRefresh: () => void;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function DocumentSection({ title, ownerId, category, documents, onRefresh }: DocumentSectionProps) {
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [renaming, setRenaming] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [renameSaving, setRenameSaving] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const uploadFile = async (file: File) => {
    setUploading(true);
    try {
      await api.uploadDocument(file, ownerId, category);
      onRefresh();
    } catch {
      alert('Error al subir el documento');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await uploadFile(file);
  };

  const handleDrop = async (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    await uploadFile(file);
  };

  const handleDelete = async (doc: OwnerDocument) => {
    if (!confirm(`¿Eliminar "${doc.fileName}"?`)) return;
    setDeleting(doc.id);
    try {
      await api.deleteDocument(doc.id, ownerId);
      onRefresh();
    } catch {
      alert('Error al eliminar el documento');
    } finally {
      setDeleting(null);
    }
  };

  const handlePreview = (doc: OwnerDocument) => {
    window.open(api.downloadDocumentUrl(doc.id, ownerId), '_blank');
  };

  const handleDownload = (doc: OwnerDocument) => {
    window.open(api.downloadDocumentUrl(doc.id, ownerId) + '&mode=download', '_blank');
  };

  const startRename = (doc: OwnerDocument) => {
    setRenaming(doc.id);
    setRenameValue(doc.fileName);
  };

  const handleRename = async (doc: OwnerDocument) => {
    if (!renameValue.trim() || renameValue.trim() === doc.fileName) {
      setRenaming(null);
      return;
    }
    setRenameSaving(true);
    try {
      await api.renameDocument(doc.id, ownerId, renameValue.trim());
      onRefresh();
    } catch {
      alert('Error al renombrar');
    } finally {
      setRenameSaving(false);
      setRenaming(null);
    }
  };

  const categoryDocs = documents.filter(d => d.category === category);

  return (
    <div className="space-y-2">
      <h4 className="text-xs font-semibold text-sereno-dark/50 uppercase tracking-wider">{title}</h4>

      <label
        className={classNames(
          'flex flex-col items-center justify-center gap-1.5 w-full rounded-xl border-2 border-dashed px-4 py-4 cursor-pointer transition-colors',
          dragOver
            ? 'border-sereno-blue bg-sereno-blue/10 text-sereno-dark/70'
            : 'border-sereno-dark/15 hover:border-sereno-blue/50 hover:bg-sereno-blue/5 text-sereno-dark/30 hover:text-sereno-dark/50',
          uploading && 'pointer-events-none opacity-60'
        )}
        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
      >
        {uploading
          ? <Loader2 size={20} className="animate-spin" />
          : <Upload size={20} />
        }
        <span className="text-xs text-center leading-tight">
          {uploading ? 'Subiendo…' : <><span className="font-medium">Arrastra un archivo</span> o haz clic</>}
        </span>
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp,.xls,.xlsx"
          onChange={handleUpload}
          disabled={uploading}
        />
      </label>
      {categoryDocs.length > 0 ? (
        <div className="space-y-1">
          {categoryDocs.map(doc => (
            <div key={doc.id} className="flex items-center gap-2 p-2 rounded-lg hover:bg-sereno-blue/10 group text-sm">
              <FileText size={14} className="text-sereno-dark/40 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                {renaming === doc.id ? (
                  <div className="flex items-center gap-1">
                    <input
                      className="input text-sm py-0.5 px-1 flex-1 min-w-0"
                      value={renameValue}
                      onChange={e => setRenameValue(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') handleRename(doc); if (e.key === 'Escape') setRenaming(null); }}
                      autoFocus
                    />
                    <button onClick={() => handleRename(doc)} disabled={renameSaving} className="p-0.5 rounded hover:bg-green-50 text-green-600" title="Guardar">
                      {renameSaving ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
                    </button>
                    <button onClick={() => setRenaming(null)} className="p-0.5 rounded hover:bg-gray-100 text-sereno-dark/40" title="Cancelar">
                      <X size={12} />
                    </button>
                  </div>
                ) : (
                  <>
                    <p className="truncate text-sm">{doc.fileName}</p>
                    <p className="text-xs text-sereno-dark/30">{formatFileSize(doc.size)}</p>
                  </>
                )}
              </div>
              <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  onClick={() => startRename(doc)}
                  className="p-1 rounded hover:bg-sereno-blue/20 text-sereno-dark/30 hover:text-sereno-dark/60"
                  title="Renombrar"
                >
                  <Pencil size={13} />
                </button>
                <button
                  onClick={() => handlePreview(doc)}
                  className="p-1 rounded hover:bg-sereno-blue/20 text-sereno-dark/30 hover:text-sereno-dark/60"
                  title="Ver"
                >
                  <Eye size={13} />
                </button>
                <button
                  onClick={() => handleDownload(doc)}
                  className="p-1 rounded hover:bg-sereno-blue/20 text-sereno-dark/30 hover:text-sereno-dark/60"
                  title="Descargar"
                >
                  <Download size={13} />
                </button>
                <button
                  onClick={() => handleDelete(doc)}
                  disabled={deleting === doc.id}
                  className="p-1 rounded hover:bg-red-50 text-sereno-dark/30 hover:text-red-500"
                  title="Eliminar"
                >
                  {deleting === doc.id ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-xs text-sereno-dark/25 py-1">Sin documentos</p>
      )}
    </div>
  );
}
